import { useState } from "react";
import { 
  GitBranch, ChevronDown, X, Search, Play,
  Zap, FileSearch, Sparkles, Brain, Plus
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Input } from "@/components/ui/input";
import { cn } from "@/lib/utils";
import { SAMPLE_WORKFLOWS } from "@/data/aiToolkit";
import { WORKFLOW_TEMPLATES } from "@/data/workflowTemplates";
import type { Workflow } from "@/types/aiToolkit";
import { WorkflowBuilder } from "@/components/WorkflowBuilder";
import { useWorkflowRun } from "@/hooks/useWorkflowRun";

const ICON_MAP: Record<string, React.ComponentType<{ className?: string }>> = {
  GitBranch,
  Zap,
  FileSearch,
  Sparkles,
  Brain,
  Play,
};

interface WorkflowSelectorProps {
  selectedWorkflow: Workflow | null;
  onWorkflowSelect: (workflow: Workflow | null) => void;
  compact?: boolean;
}

export function WorkflowSelector({ 
  selectedWorkflow, 
  onWorkflowSelect, 
  compact = false 
}: WorkflowSelectorProps) {
  const [open, setOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [showBuilder, setShowBuilder] = useState(false);
  const [editingWorkflow, setEditingWorkflow] = useState<Workflow | null>(null);
  const { startWorkflow } = useWorkflowRun();

  const allWorkflows = [...WORKFLOW_TEMPLATES, ...SAMPLE_WORKFLOWS];

  const filteredWorkflows = allWorkflows.filter((workflow) => {
    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase();
      return (
        workflow.name.toLowerCase().includes(query) ||
        workflow.description.toLowerCase().includes(query) ||
        workflow.tags.some((t) => t.toLowerCase().includes(query)) ||
        workflow.category.toLowerCase().includes(query)
      );
    }
    return true;
  });

  const handleSelect = (workflow: Workflow) => {
    onWorkflowSelect(workflow);
    setOpen(false);
    setSearchQuery("");
  };

  const handleClear = (e: React.MouseEvent) => {
    e.stopPropagation();
    onWorkflowSelect(null);
  };

  const SelectedIcon = selectedWorkflow 
    ? ICON_MAP[selectedWorkflow.icon] || GitBranch 
    : null;

  return (
    <>
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant={selectedWorkflow ? "default" : "outline"}
          size={compact ? "sm" : "default"}
          className={cn(
            "w-full justify-between gap-2",
            compact ? "h-8 px-2" : "h-8 text-sm",
            selectedWorkflow && "bg-orange-600 hover:bg-orange-700"
          )}
          data-testid="select-workflow"
        >
          {selectedWorkflow ? (
            <>
              {SelectedIcon && <SelectedIcon className="h-3.5 w-3.5" />}
              <span className="truncate flex-1 text-left">{selectedWorkflow.name}</span>
              <X 
                className="h-3 w-3 hover:bg-white/20 rounded" 
                onClick={handleClear}
              />
            </>
          ) : (
            <>
              <GitBranch className="h-3.5 w-3.5" />
              <span className="flex-1 text-left">Select Workflow</span>
              <ChevronDown className="h-3 w-3" />
            </>
          )}
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-[320px] p-0" align="start">
        <div className="p-2 border-b">
          <div className="relative">
            <Search className="absolute left-2.5 top-1/2 transform -translate-y-1/2 h-3.5 w-3.5 text-muted-foreground" />
            <Input
              placeholder="Search workflows..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-8 h-8 text-sm"
              data-testid="input-search-workflows"
            />
          </div>
        </div>
        
        <ScrollArea className="max-h-[280px]">
          <div className="p-2 space-y-1">
            {filteredWorkflows.length === 0 ? (
              <div className="text-center py-6 text-muted-foreground text-sm">
                <GitBranch className="h-8 w-8 mx-auto mb-2 opacity-50" />
                <p>No workflows found</p>
              </div>
            ) : (
              filteredWorkflows.map((workflow) => {
                const Icon = ICON_MAP[workflow.icon] || GitBranch;
                return (
                  <button
                    key={workflow.id}
                    onClick={() => handleSelect(workflow)}
                    className={cn(
                      "w-full flex items-start gap-2 p-2 rounded-md text-left transition-colors hover-elevate",
                      selectedWorkflow?.id === workflow.id && "bg-accent"
                    )}
                    data-testid={`button-workflow-${workflow.id}`}
                  >
                    <div className={cn("p-1.5 rounded-md flex-shrink-0", workflow.color)}>
                      <Icon className="h-3.5 w-3.5 text-white" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-1.5">
                        <span className="font-medium text-sm truncate">{workflow.name}</span>
                        {workflow.access === 'pro' && (
                          <Badge variant="outline" className="text-[9px] h-4 px-1">PRO</Badge>
                        )}
                      </div>
                      <p className="text-xs text-muted-foreground line-clamp-2 mt-0.5">
                        {workflow.description}
                      </p>
                      <div className="flex items-center gap-1 mt-1">
                        <Badge variant="secondary" className="text-[9px] h-4 px-1">
                          {workflow.nodes.length} nodes
                        </Badge>
                        <Badge variant="secondary" className="text-[9px] h-4 px-1">
                          {workflow.category}
                        </Badge>
                      </div>
                    </div>
                  </button>
                );
              })
            )}
          </div>
        </ScrollArea>
        
        <div className="p-2 border-t flex items-center justify-between gap-2">
          <p className="text-[10px] text-muted-foreground">
            Workflows automate multi-step AI tasks
          </p>
          <Button
            variant="outline"
            size="sm"
            className="h-6 px-2 text-[10px]"
            onClick={() => {
              setOpen(false);
              setEditingWorkflow(null);
              setShowBuilder(true);
            }}
            data-testid="button-create-workflow"
          >
            <Plus className="h-3 w-3 mr-1" />
            Create
          </Button>
        </div>
      </PopoverContent>
    </Popover>

    {/* Workflow Builder Dialog */}
    <Dialog open={showBuilder} onOpenChange={setShowBuilder}>
      <DialogContent className="max-w-5xl h-[80vh] p-0 flex flex-col">
        <DialogHeader className="p-4 pb-0">
          <DialogTitle className="flex items-center gap-2">
            <GitBranch className="h-5 w-5" />
            {editingWorkflow ? 'Edit Workflow' : 'Create Workflow'}
          </DialogTitle>
        </DialogHeader>
        <div className="flex-1 overflow-hidden">
          <WorkflowBuilder 
            workflow={editingWorkflow || undefined}
            onSave={() => {
              setShowBuilder(false);
            }}
            onRun={(wf) => {
              void startWorkflow(wf as Workflow, { executionMode: "manual" });
              setShowBuilder(false);
            }}
          />
        </div>
      </DialogContent>
    </Dialog>
    </>
  );
}
